import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Dialog } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import { GripVertical, Lock, Trash2 } from 'lucide-react';
import { useCRM } from '../../../hooks/crm';

const EditColumnsModel = ({ isOpen, onClose, board }) => {
  const { t } = useTranslation();
  const { saveBoardColumns } = useCRM();
  const [columns, setColumns] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const sortedColumns = [...(board?.columns || [])].sort((left, right) => left.position - right.position);
    setColumns(sortedColumns);
  }, [board?.columns, isOpen]);

  const handleRenameColumn = (columnId, newName) => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => (
        column.column_id === columnId
          ? { ...column, column_name: newName }
          : column
      ))
    );
  };

  const handleOnDragEnd = (result) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index || destination.index === 0) {
      return;
    }

    const reorderedColumns = [...columns];
    const [movedColumn] = reorderedColumns.splice(source.index, 1);
    reorderedColumns.splice(destination.index, 0, movedColumn);

    setColumns(reorderedColumns.map((column, index) => ({
      ...column,
      position: index + 1,
    })));
  };

  const handleDeleteColumn = (columnId) => {
    setColumns((prevColumns) =>
      prevColumns.filter((column) => column.column_id !== columnId)
        .map((column, index) => ({
          ...column,
          position: index + 1,
        }))
    );
  };

  const handleSaveChanges = async () => {
    if (!board?.board_id) {
      return;
    }

    setIsSaving(true);

    try {
      await saveBoardColumns(board.board_id, columns);
      onClose();
    } catch (error) {
      console.error('Error updating columns:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('app:crm.t23')}
      subtitle={board?.board_name}
      size="2xl"
      stickyFooter
      closeOnOverlayClick
      overlayClassName="bg-white/10 backdrop-blur-[2px]"
      contentClassName="rounded-2xl border border-stone-200 shadow-2xl"
      bodyClassName="space-y-4"
      footerClassName="bg-white/90 backdrop-blur-[6px]"
      footerActions={(
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-full border-stone-200 bg-white px-4 text-xs text-stone-700 hover:bg-stone-50"
          >
            {t('app:crm.t5')}
          </Button>
          <Button
            size="sm"
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="rounded-full px-4 text-xs"
          >
            {isSaving ? t('app:crm.t26') : t('app:crm.t27')}
          </Button>
        </div>
      )}
    >
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="columns">
          {(provided) => (
            <ul
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {columns.map((column, index) => (
                <Draggable
                  key={column.column_id}
                  draggableId={String(column.column_id)}
                  index={index}
                  isDragDisabled={column.column_id === 1}
                >
                  {(draggableProvided) => (
                    <li
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      className={cn(
                        'flex items-center gap-3 rounded-xl border px-3 py-3 transition-colors',
                        column.column_id === 1
                          ? 'border-stone-200 bg-stone-50/80'
                          : 'border-stone-200 bg-white hover:border-stone-300'
                      )}
                    >
                      <span
                        {...(column.column_id !== 1 ? draggableProvided.dragHandleProps : {})}
                        className={cn(
                          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-400',
                          column.column_id === 1 ? 'cursor-not-allowed opacity-60' : 'cursor-grab'
                        )}
                      >
                        {column.column_id === 1 ? (
                          <Lock className="h-4 w-4" />
                        ) : (
                          <GripVertical className="h-4 w-4" />
                        )}
                      </span>

                      <Input
                        multi={false}
                        value={column.column_name}
                        onChange={(event) => handleRenameColumn(column.column_id, event.target.value)}
                        className="w-full border-stone-200 bg-white shadow-none"
                      />

                      <div className="flex items-center">
                        {column.column_id === 1 ? (
                          <span className="rounded-full border border-stone-200 bg-white px-3 py-1 text-[11px] font-medium text-stone-500">
                            {t('app:crm.t24')}
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteColumn(column.column_id)}
                            iconLeft={<Trash2 className="h-3.5 w-3.5" />}
                            className="rounded-full px-3 text-xs text-stone-500 hover:bg-stone-100 hover:text-stone-900"
                          >
                            {t('app:crm.t25')}
                          </Button>
                        )}
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </Dialog>
  );
};

export default EditColumnsModel;
